import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Space,
  Alert,
  Table,
  Tag,
  Spin,
  Empty,
  message,
  Divider,
  Input,
  Button,
  Form,
  Select,
  Modal,
  Descriptions,
  Tabs,
  Tooltip,
  Grid,
  AutoComplete
} from 'antd';
import {
  BarChartOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  CloudDownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import dayjs from 'dayjs';
import { retentionDaysOf, retentionDisabledDate, clampRange, RetentionHint } from '../../../shared/components/cdn/PlanGate';
import * as echarts from 'echarts';
import worldJson from '../../../shared/components/world.json';
import CountryFlag, { CountryLabel } from '../../../shared/components/CountryFlag';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

// 注册世界地图
echarts.registerMap('world', worldJson);

// 国家代码到 ECharts 地图名称的映射（必须与 world.json 中的名称一致）
const countryCodeToEchartsName = {
  'CN': 'China', 'US': 'United States', 'JP': 'Japan', 'KR': 'Korea',
  'DE': 'Germany', 'FR': 'France', 'GB': 'United Kingdom', 'RU': 'Russia',
  'BR': 'Brazil', 'IN': 'India', 'AU': 'Australia', 'CA': 'Canada',
  'SG': 'Singapore', 'HK': 'Hong Kong', 'TW': 'Taiwan', 'NL': 'Netherlands',
  'MY': 'Malaysia', 'BG': 'Bulgaria', 'IE': 'Ireland', 'SE': 'Sweden',
  'BE': 'Belgium', 'IT': 'Italy', 'ES': 'Spain', 'PT': 'Portugal',
  'PL': 'Poland', 'UA': 'Ukraine', 'CZ': 'Czech Rep.', 'AT': 'Austria',
  'CH': 'Switzerland', 'NO': 'Norway', 'FI': 'Finland', 'DK': 'Denmark',
  'GR': 'Greece', 'TR': 'Turkey', 'IL': 'Israel', 'SA': 'Saudi Arabia',
  'AE': 'United Arab Emirates', 'TH': 'Thailand', 'VN': 'Vietnam',
  'ID': 'Indonesia', 'PH': 'Philippines', 'NZ': 'New Zealand',
  'MX': 'Mexico', 'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia',
  'ZA': 'South Africa', 'EG': 'Egypt', 'NG': 'Nigeria', 'KE': 'Kenya'
};

// API 返回的名称到 ECharts 名称的映射
const apiNameToEchartsName = {
  'United States of America': 'United States',
  'The Netherlands': 'Netherlands',
  'Korea, Republic of': 'Korea',
  'Russian Federation': 'Russia',
  'Viet Nam': 'Vietnam',
  'Czechia': 'Czech Rep.'
};

export default function Traffic() {
  const { t } = useTranslation();
  const { domainData } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  // 套餐日志保留天数:日期只能选保留期内
  const retDays = retentionDaysOf(domainData);
  useEffect(() => {
    if (retDays) setDateRange((r) => clampRange(r, retDays));
  }, [retDays]);
  const [trafficData, setTrafficData] = useState(null);
  const [topUrls, setTopUrls] = useState([]);
  const [statusCodes, setStatusCodes] = useState([]);
  const [geoData, setGeoData] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(20);
  const [logsTotal, setLogsTotal] = useState(0);
  const [lastSearchValues, setLastSearchValues] = useState({});
  const [logDetail, setLogDetail] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchForm] = Form.useForm();

  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (domainData?.domain && dateRange && dateRange[0] && dateRange[1]) {
      loadTrafficStats();
    }
  }, [domainData, dateRange]);

  // 初始化和管理地图（带重试，确保容器可见后再初始化）
  useEffect(() => {
    if (activeTab !== 'map' || geoData.length === 0) return;

    let timer = null;
    let resizeHandler = null;
    let cancelled = false;

    const tryInit = (attempt = 0) => {
      if (cancelled) return;
      if (mapRef.current && mapRef.current.offsetWidth > 0 && mapRef.current.offsetHeight > 0) {
        initMap();
        resizeHandler = () => mapInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
      } else if (attempt < 20) {
        timer = setTimeout(() => tryInit(attempt + 1), 50);
      }
    };

    timer = setTimeout(() => tryInit(0), 50);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      if (mapInstance.current && activeTab !== 'map') {
        mapInstance.current.dispose();
        mapInstance.current = null;
      }
    };
  }, [activeTab, geoData]);

  // 切换到请求查询 tab 时自动加载日志
  useEffect(() => {
    if (activeTab === 'logs' && logs.length === 0 && domainData?.id && dateRange?.[0]) {
      searchLogs({});
    }
  }, [activeTab]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.dispose();
        mapInstance.current = null;
      }
    };
  }, []);

  const initMap = () => {
    if (!mapRef.current) return;

    // 确保容器有正确的尺寸
    const container = mapRef.current;
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      return;
    }

    if (mapInstance.current) {
      mapInstance.current.dispose();
      mapInstance.current = null;
    }

    mapInstance.current = echarts.init(container);

    // 转换国家名称为 ECharts 地图识别的名称
    const getEchartsName = (item) => {
      // 优先使用国家代码映射
      if (item.country_code && countryCodeToEchartsName[item.country_code]) {
        return countryCodeToEchartsName[item.country_code];
      }
      // 其次使用 API 名称映射
      if (item.country_name && apiNameToEchartsName[item.country_name]) {
        return apiNameToEchartsName[item.country_name];
      }
      // 最后使用原始名称
      return item.country_name || item.country_code || 'Unknown';
    };

    const mapData = geoData.map(item => ({
      name: getEchartsName(item),
      value: item.requests || 0,
      traffic: item.traffic,
      originalName: item.country_name,
      ...item
    }));

    const scatterData = geoData
      .filter(item => item.latitude && item.longitude)
      .map(item => ({
        name: item.country_name || item.country_code,
        value: [parseFloat(item.longitude), parseFloat(item.latitude), item.requests || 0],
        traffic: item.traffic
      }));

    const maxValue = Math.max(...mapData.map(d => d.value), 1);

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          if (params.seriesType === 'scatter') {
            return t('<strong>{{name}}</strong><br/>\n                    Requests: {{value}}<br/>\n                    Traffic: {{value2}}', { name: params.data.name, value: (params.data.value[2] || 0).toLocaleString(), value2: formatBytes(params.data.traffic) });
          }
          if (params.value) {
            const displayName = params.data?.originalName || params.name;
            return t('<strong>{{displayName}}</strong><br/>Requests: {{value}}<br/>Traffic: {{value2}}', { displayName, value: (params.value || 0).toLocaleString(), value2: formatBytes(params.data?.traffic || 0) });
          }
          return t('<strong>{{name}}</strong><br/>No data', { name: params.name });
        }
      },
      visualMap: {
        min: 0,
        itemWidth: 10,
        itemHeight: 80,
        max: maxValue,
        text: [t('High'), t('Low')],
        calculable: false,
        inRange: { color: ['#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695'] },
        left: 'left',
        top: 'bottom'
      },
      geo: {
        map: 'world',
        roam: true,
        zoom: 1.2,
        scaleLimit: { min: 1, max: 10 },
        itemStyle: { areaColor: '#f5f5f5', borderColor: '#d9d9d9', borderWidth: 0.5 },
        emphasis: {
          itemStyle: { areaColor: '#ffd666' },
          label: { show: true }
        }
      },
      series: [
        {
          name: t('Requests'),
          type: 'map',
          map: 'world',
          geoIndex: 0,
          data: mapData
        },
        ...(scatterData.length > 0 ? [{
          name: t('Location'),
          type: 'scatter',
          coordinateSystem: 'geo',
          data: scatterData,
          symbolSize: (val) => 4 + 12 * Math.sqrt((val[2] || 0) / maxValue), // 按最大值归一,直径 4~16px
          itemStyle: {
            color: 'rgba(22, 119, 255, 0.75)',
            borderColor: '#fff',
            borderWidth: 1
          }
        }] : [])
      ]
    };

    mapInstance.current.setOption(option);

    // 延迟 resize 确保渲染完成
    setTimeout(() => {
      mapInstance.current?.resize();
    }, 200);

    const handleResize = () => mapInstance.current?.resize();
    window.addEventListener('resize', handleResize);

    // 返回清理函数（虽然在这个位置不会被调用，但保留以备后用）
  };

  const loadTrafficStats = async () => {
    setLoading(true);
    try {
      const startTime = dateRange[0].unix(); // 发 Unix 秒:服务端按 Asia/Shanghai 解析字符串,非东八区用户会偏移时间窗
      const endTime = dateRange[1].unix();

      const [trafficRes, topUrlsRes, statusRes, mapRes] = await Promise.all([
        apiClient.get(`/api/user/cdn/domains/${domainData.domain}/traffic`, {
          params: { startTime, endTime }
        }),
        apiClient.get('/api/user/traffic/top-urls', {
          params: { startTime, endTime, domain_id: domainData.id }
        }),
        apiClient.get('/api/user/traffic/status-codes', {
          params: { startTime, endTime, domain_id: domainData.id }
        }),
        apiClient.get('/api/user/traffic/map', {
          params: { startTime, endTime, domain_id: domainData.id }
        })
      ]);

      if (trafficRes.data.success) setTrafficData(trafficRes.data.data);
      if (topUrlsRes.data.success) setTopUrls(topUrlsRes.data.data || []);
      if (statusRes.data.success) setStatusCodes(statusRes.data.data || []);
      if (mapRes.data.success) setGeoData(mapRes.data.data || []);
    } catch (error) {
      console.error('Failed to load traffic stats:', error);
      message.error(t('Failed to load traffic statistics: {{value}}', { value: error.response?.data?.error || error.message }));
    } finally {
      setLoading(false);
    }
  };

  const searchLogs = async (values, page = 1, pageSize = logsPageSize) => {
    setLogsLoading(true);
    try {
      const startTime = dateRange[0].unix(); // 发 Unix 秒:服务端按 Asia/Shanghai 解析字符串,非东八区用户会偏移时间窗
      const endTime = dateRange[1].unix();

      const params = {
        startTime,
        endTime,
        domain_id: domainData.id,
        page,
        pageSize,
        ...values
      };

      const response = await apiClient.get('/api/user/traffic/logs', { params });
      if (response.data.success) {
        setLogs(response.data.data || []);
        setLogsTotal(response.data.total || 0);
        setLogsPage(page);
        setLogsPageSize(pageSize);
        setLastSearchValues(values || {});
        if (response.data.data?.length === 0 && page === 1) {
          message.info(t('No matching request logs found'));
        }
      }
    } catch (error) {
      message.error(t('Query failed: {{value}}', { value: error.response?.data?.error || error.message }));
    } finally {
      setLogsLoading(false);
    }
  };

  const viewLogDetail = async (record) => {
    // 优先使用已有数据，避免重复请求
    if (record) {
      setLogDetail(record);
      setDetailModalVisible(true);

      // 如果需要更多详情字段，再异步获取
      try {
        const domainId = record.domain_id || domainData.id;
        const response = await apiClient.get(`/api/user/traffic/log/${domainId}/${record.request_id}`);
        if (response.data.success) {
          setLogDetail(response.data.data);
        }
      } catch (error) {
        console.error('获取详情失败:', error);
      }
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num) => new Intl.NumberFormat('zh-CN').format(num || 0);

  const urlColumns = [
    { title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true },
    { title: t('Visits'), dataIndex: 'hits', key: 'hits', render: formatNumber, sorter: (a, b) => (a.hits || 0) - (b.hits || 0) },
    { title: t('Traffic'), dataIndex: 'bytes', key: 'bytes', render: formatBytes, sorter: (a, b) => (a.bytes || 0) - (b.bytes || 0) }
  ];

  const statusColumns = [
    {
      title: t('HTTP status code'), dataIndex: 'status', key: 'status',
      render: (status) => {
        let color = 'default';
        if (status >= 200 && status < 300) color = 'success';
        else if (status >= 300 && status < 400) color = 'processing';
        else if (status >= 400 && status < 500) color = 'warning';
        else if (status >= 500) color = 'error';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    { title: t('Count'), dataIndex: 'count', key: 'count', render: formatNumber, sorter: (a, b) => (a.count || 0) - (b.count || 0) }
  ];

  const logColumns = [
    {
      title: t('Time'), dataIndex: 'timestamp', key: 'timestamp', width: 170,
      render: (t) => t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    { title: t('Domain'), dataIndex: 'host', key: 'host', width: 170, ellipsis: true, render: (h) => h || '-' },
    { title: t('Method'), dataIndex: 'method', key: 'method', width: 70 },
    {
      title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true,
      // 后端已直接返回完整 url(path + query);兜底再从 uri+args 拼(旧数据)。
      render: (url, r) => {
        const full = url || ((r.uri || '/') + (r.args && r.args !== '-' ? `?${r.args}` : ''));
        return <span title={full}>{full}</span>;
      }
    },
    {
      title: t('Status'), dataIndex: 'status', key: 'status', width: 90,
      render: (status, r) => {
        let color = status >= 200 && status < 300 ? 'success' : status >= 500 ? 'error' : 'warning';
        return (
          <>
            <Tag color={color}>{status}</Tag>
          </>
        );
      }
    },
    { title: t('HTTP version'), dataIndex: 'http_version', key: 'http_version', width: 90, render: (v) => v || '-' },
    { title: t('Size'), dataIndex: 'bytes', key: 'bytes', width: 90, render: formatBytes },
    { title: t('Time taken'), dataIndex: 'request_time', key: 'request_time', width: 80, render: (t) => `${(t || 0).toFixed(3)}s` },
    { title: t('Client IP'), dataIndex: 'client_ip', key: 'client_ip', width: 130 },
    { title: t('Country'), dataIndex: 'country_code', key: 'country_code', width: 80, render: (c) => (c ? <span style={{ whiteSpace: 'nowrap' }}><CountryFlag code={c} />{c}</span> : '-') },
    { title: t('Type'), dataIndex: 'resp_content_type', key: 'resp_content_type', width: 100, ellipsis: true, render: (t) => t ? t.split(';')[0] : '-' },
    { title: t('Cache'), dataIndex: 'cache_status', key: 'cache_status', width: 70, render: (s) => <Tag color={s === 'HIT' ? 'green' : 'orange'}>{s || '-'}</Tag> },
    {
      title: t('Actions'), key: 'action', width: 60, fixed: 'right',
      render: (_, record) => (
        <Tooltip title={t('View details')}>
          <Button type="link" icon={<EyeOutlined />} onClick={() => viewLogDetail(record)} />
        </Tooltip>
      )
    }
  ];

  const overview = trafficData?.overview || {};

  const tabItems = [
    {
      key: 'overview',
      label: t('Overview statistics'),
      children: (
        <Spin spinning={loading}>
          {/* 概览统计 */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title={t('Total requests')}
                  value={formatNumber(overview.total_requests || 0)}
                  prefix={<GlobalOutlined />}
                  valueStyle={{ color: '#1890ff', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title={t('Total traffic')}
                  value={formatBytes(overview.total_traffic || 0)}
                  prefix={<CloudDownloadOutlined />}
                  valueStyle={{ color: '#3f8600', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title={t('Cache hit ratio')}
                  value={(overview.cache_hit_rate || 0).toFixed(1)}
                  suffix="%"
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ color: (overview.cache_hit_rate || 0) > 80 ? '#3f8600' : '#cf1322', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title={isMobile ? t('Response time') : t('Average response time')}
                  value={((overview.avg_response_time || 0) * 1000).toFixed(0)}
                  suffix="ms"
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: (overview.avg_response_time || 0) < 0.2 ? '#3f8600' : '#cf1322', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
          </Row>

          {/* 状态码分布 */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '2xx' : t('2xx Success')} value={formatNumber(overview.status_2xx || 0)} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '3xx' : t('3xx Redirection')} value={formatNumber(overview.status_3xx || 0)} prefix={<GlobalOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '4xx' : t('4xx Client error')} value={formatNumber(overview.status_4xx || 0)} prefix={<WarningOutlined style={{ color: '#faad14' }} />} valueStyle={{ color: '#faad14', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '5xx' : t('5xx Server error')} value={formatNumber(overview.status_5xx || 0)} prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />} valueStyle={{ color: '#ff4d4f', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
          </Row>

          <Divider style={{ margin: isMobile ? '12px 0' : '24px 0' }} />

          {/* 详细表格 */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={24} lg={12}>
              <Card title={t('Top URLs')} size="small">
                {topUrls.length > 0 ? (
                  <Table
                    columns={urlColumns}
                    dataSource={topUrls}
                    rowKey="url"
                    pagination={{ pageSize: isMobile ? 5 : 10, size: 'small' }}
                    size="small"
                    scroll={isMobile ? { x: 400 } : undefined}
                  />
                ) : (
                  <Empty description={t('No data yet')} />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title={t('HTTP status code distribution')} size="small">
                {statusCodes.length > 0 ? (
                  <Table
                    columns={statusColumns}
                    dataSource={statusCodes}
                    rowKey="status"
                    pagination={{ pageSize: isMobile ? 5 : 10, size: 'small' }}
                    size="small"
                  />
                ) : (
                  <Empty description={t('No data yet')} />
                )}
              </Card>
            </Col>
          </Row>
        </Spin>
      )
    },
    {
      key: 'map',
      label: t('Geographic distribution'),
      children: (
        <Card title={t('Global request distribution')} size="small">
          {geoData.length > 0 ? (
            <div ref={mapRef} style={{ width: '100%', height: isMobile ? 300 : 500 }} />
          ) : (
            <Empty description={t('No geographic data yet')} style={{ padding: isMobile ? 50 : 100 }} />
          )}
          {geoData.length > 0 && (
            <Table
              columns={[
                {
                  title: t('Country/Region'),
                  dataIndex: 'country_code',
                  key: 'country',
                  ellipsis: true,
                  render: (code, r) => {
                    let name = r.country_name || code || t('Unknown');
                    if (code && countryCodeToEchartsName[code]) name = countryCodeToEchartsName[code];
                    else if (r.country_name && apiNameToEchartsName[r.country_name]) name = apiNameToEchartsName[r.country_name];
                    return <CountryLabel code={code}>{name}</CountryLabel>;
                  }
                },
                { title: t('Requests'), dataIndex: 'requests', key: 'requests', render: formatNumber, sorter: (a, b) => (a.requests || 0) - (b.requests || 0) },
                { title: t('Traffic'), dataIndex: 'traffic', key: 'traffic', render: formatBytes, sorter: (a, b) => (a.traffic || 0) - (b.traffic || 0) }
              ]}
              dataSource={geoData.filter(r => r.country_code)}
              rowKey={(r) => r.country_code}
              pagination={{ pageSize: isMobile ? 5 : 10, size: 'small' }}
              size="small"
              style={{ marginTop: 16 }}
              scroll={isMobile ? { x: 350 } : undefined}
            />
          )}
        </Card>
      )
    },
    {
      key: 'logs',
      label: t('Request query'),
      children: (
        <Card title={t('Search request logs')} size="small">
          <Form
            form={searchForm}
            layout="vertical"
            onFinish={searchLogs}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[12, 0]}>
              <Col xs={12} sm={8} md={6}>
                <Form.Item name="host" label={t('Hostname')}>
                  <AutoComplete
                    allowClear
                    placeholder={t('All hostnames')}
                    options={(domainData?.hostnames || []).map((h) => ({ value: h.hostname }))}
                    filterOption={(input, o) => String(o.value).toLowerCase().includes(String(input).toLowerCase())}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Form.Item name="request_id" label="Request ID">
                  <Input placeholder={t('Request ID')} allowClear />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Form.Item name="uri" label="URI">
                  <Input placeholder={t('URI path')} allowClear />
                </Form.Item>
              </Col>
              <Col xs={8} sm={4} md={3}>
                <Form.Item name="status" label={t('Status code')}>
                  <Select placeholder={t('Select')} allowClear>
                    <Option value={200}>200</Option>
                    <Option value={301}>301</Option>
                    <Option value={302}>302</Option>
                    <Option value={304}>304</Option>
                    <Option value={400}>400</Option>
                    <Option value={403}>403</Option>
                    <Option value={404}>404</Option>
                    <Option value={500}>500</Option>
                    <Option value={502}>502</Option>
                    <Option value={503}>503</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={8} sm={6} md={4}>
                <Form.Item name="client_ip" label={t('Client IP')}>
                  <Input placeholder={t('IP address')} allowClear />
                </Form.Item>
              </Col>
              <Col xs={8} sm={6} md={6}>
                <Form.Item label=" ">
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={logsLoading}>{t('Query')}</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => { searchForm.resetFields(); setLogs([]); }}>{t('Reset')}</Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Table
            columns={logColumns}
            dataSource={logs}
            rowKey="request_id"
            loading={logsLoading}
            pagination={{
              current: logsPage,
              pageSize: logsPageSize,
              total: logsTotal,
              showSizeChanger: !isMobile,
              pageSizeOptions: ['20', '50', '100', '200'],
              showTotal: isMobile ? undefined : (total) => t('{{total}} records total', { total }),
              size: 'small',
              onChange: (page, pageSize) => {
                searchLogs(lastSearchValues, page, pageSize);
              },
            }}
            size="small"
            scroll={{ x: 1460 }}
          />
        </Card>
      )
    }
  ];

  return (
    <div>
      <Card
        title={
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            <span><BarChartOutlined />{t(' Traffic analytics')}</span>
            {!isMobile && <span style={{ color: '#666', fontWeight: 'normal' }}>- {domainData?.domain}</span>}
          </Space>
        }
        extra={!isMobile && (
          <Space size={8}>
            <RetentionHint days={retDays} />
            <RangePicker
              value={dateRange}
              onChange={(r) => setDateRange(clampRange(r, retDays))}
              disabledDate={retentionDisabledDate(retDays)}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder={[t('Start date'), t('End date')]}
            />
          </Space>
        )}
        size={isMobile ? 'small' : 'default'}
      >
        {/* 移动端显示日期选择器 */}
        {isMobile && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>{t('Domain: {{value}}', { value: domainData?.domain })}</div>
            <RangePicker
              value={dateRange}
              onChange={(r) => setDateRange(clampRange(r, retDays))}
              disabledDate={retentionDisabledDate(retDays)}
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder={[t('Start'), t('End')]}
              style={{ width: '100%' }}
              size="small"
            />
            <RetentionHint days={retDays} />
          </div>
        )}

        <Alert
          message={t('Traffic statistics')}
          description={isMobile ? t('View domain traffic usage and request logs') : t('View domain traffic usage, geographic distribution, and request logs. Data comes from OpenObserve log analytics.')}
          type="info"
          showIcon
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* 请求详情弹窗 */}
      <Modal
        title={t('Request details')}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={isMobile ? { top: 20 } : undefined}
      >
        {logDetail && (
          <Tabs defaultActiveKey="basic" size={isMobile ? 'small' : 'middle'} items={[
            {
              key: 'basic',
              label: t('Basic information'),
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="Request ID" span={isMobile ? 1 : 2}><span style={{ wordBreak: 'break-all', fontSize: isMobile ? 11 : 14 }}>{logDetail.request_id}</span></Descriptions.Item>
                  <Descriptions.Item label={t('Time')}>{logDetail.timestamp ? dayjs(logDetail.timestamp).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Domains')}><span style={{ wordBreak: 'break-all' }}>{logDetail.host}</span></Descriptions.Item>
                  <Descriptions.Item label={t('Method')}>{logDetail.method}</Descriptions.Item>
                  <Descriptions.Item label={t('Status code')}><Tag color={logDetail.status >= 200 && logDetail.status < 300 ? 'success' : logDetail.status >= 500 ? 'error' : 'warning'}>{logDetail.status}</Tag></Descriptions.Item>
                  <Descriptions.Item label="URL" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all' }}>{logDetail.url || ((logDetail.uri || '/') + (logDetail.args && logDetail.args !== '-' ? `?${logDetail.args}` : '')) || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label={t('Protocol')}>{logDetail.scheme || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('HTTP version')}>{logDetail.http_version || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Client IP')}>{logDetail.client_ip}</Descriptions.Item>
                  <Descriptions.Item label={t('Response size')}>{formatBytes(logDetail.bytes)}</Descriptions.Item>
                  <Descriptions.Item label={t('Request size')}>{logDetail.request_length ? formatBytes(logDetail.request_length) : '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Request duration')}>{(logDetail.request_time || 0).toFixed(3)}s</Descriptions.Item>
                  <Descriptions.Item label={t('Cache status')}><Tag color={logDetail.cache_status === 'HIT' ? 'green' : 'orange'}>{logDetail.cache_status || '-'}</Tag></Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'headers',
              label: t('Request headers'),
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="User-Agent" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all', fontSize: isMobile ? 11 : 14 }}>{logDetail.user_agent || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="Referer" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all' }}>{logDetail.referer || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="X-Forwarded-For" span={isMobile ? 1 : 2}>{logDetail.x_forwarded_for || '-'}</Descriptions.Item>
                  <Descriptions.Item label="X-Real-IP">{logDetail.x_real_ip || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Origin">{logDetail.origin_header || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Accept-Language">{logDetail.accept_language || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Accept-Encoding">{logDetail.accept_encoding || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Content-Type">{logDetail.content_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Range">{logDetail.range || '-'}</Descriptions.Item>
                  <Descriptions.Item label="If-Modified-Since">{logDetail.if_modified_since || '-'}</Descriptions.Item>
                  <Descriptions.Item label="If-None-Match">{logDetail.if_none_match || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'response',
              label: t('Response headers'),
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="Content-Type" span={isMobile ? 1 : 2}>{logDetail.resp_content_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Cache-Control" span={isMobile ? 1 : 2}>{logDetail.resp_cache_control || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Content-Length">{logDetail.resp_content_length || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ETag">{logDetail.resp_etag || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Last-Modified" span={isMobile ? 1 : 2}>{logDetail.resp_last_modified || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Upstream Content-Type')}>{logDetail.up_content_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Upstream Cache-Control')}>{logDetail.up_cache_control || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Upstream X-Cache')}>{logDetail.up_x_cache || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'upstream',
              label: t('Upstream'),
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label={t('Upstream address')}>{logDetail.upstream_addr || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Upstream status')}>{logDetail.upstream_status || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Response time')}>{logDetail.upstream_response_time ? `${logDetail.upstream_response_time}s` : '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Connection time')}>{logDetail.upstream_connect_time ? `${logDetail.upstream_connect_time}s` : '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Header time')}>{logDetail.upstream_header_time ? `${logDetail.upstream_header_time}s` : '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'ssl',
              label: 'SSL',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label={t('SSL protocol')}>{logDetail.ssl_protocol || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Cipher suites')}>{logDetail.ssl_cipher || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('JA3 fingerprint')} span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all', fontSize: isMobile ? 10 : 12 }}>{logDetail.ja3 || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="JA3 Hash">{logDetail.ja3_hash || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('JA4 fingerprint')} span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all', fontSize: isMobile ? 10 : 12 }}>{logDetail.ja4 || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label={t('HTTP/2 fingerprint')}>{logDetail.h2_fp || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'geo',
              label: t('Geography'),
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label={t('Country')}><CountryFlag code={logDetail.country_code} />{logDetail.country_name || '-'} ({logDetail.country_code || '-'})</Descriptions.Item>
                  <Descriptions.Item label={t('Region')}>{logDetail.region || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('City')}>{logDetail.city || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ISP">{logDetail.isp || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ASN">{logDetail.asn || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('AS name')}>{logDetail.as_name || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'cdn',
              label: 'CDN',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label={t('Node')}>{logDetail.pop || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('Cache status')}><Tag color={logDetail.cache_status === 'HIT' ? 'green' : 'orange'}>{logDetail.cache_status || '-'}</Tag></Descriptions.Item>
                </Descriptions>
              )
            }
          ]} />
        )}
      </Modal>
    </div>
  );
}
